/**
 * Related Variations Web Component
 *
 * Groups products that share the same `custom.related_variations` metafield value
 * and renders them as size and color links on the PDP.
 *
 * Discovery: Shopify Predictive Search + Section Rendering API
 *   - Queries products via /search/suggest
 *   - Renders section "related-variations-tags" with predictive_search data
 *   - No Storefront API token required
 */

class RelatedVariations extends HTMLElement {
  connectedCallback() {
    this.refCode = (this.dataset.refCode || '').trim();
    if (!this.refCode) return;

    this.display = this.dataset.display || 'thumbnail';
    this.swatchSize = parseInt(this.dataset.swatchSize || 40, 10);
    this.currentHandle = this.dataset.currentHandle;
    this.currentVariantId = String(this.dataset.currentVariantId || '');
    this.currentSizeRaw = (this.dataset.currentSize || '').trim();
    this.currentSizeNormalized = this.normalizeOptionValue(this.currentSizeRaw);

    this.fieldset = this.querySelector('fieldset');
    this.titleEl = this.querySelector('.rv-selected-title');
    this.sizeListEl = this.querySelector('.related-variations-size-list');
    this.currentColorRaw =
      (this.dataset.currentColor || '').trim() || this.extractVariationName(this.titleEl?.textContent || '');
    this.currentTitle = this.currentColorRaw || '';
    this.currentColorNormalized = this.normalizeOptionValue(this.currentColorRaw);

    if (this.titleEl && this.currentTitle) {
      this.titleEl.textContent = this.currentTitle;
    }

    this.setLoading(true);
    this.fetchRelatedProducts();
  }

  async fetchRelatedProducts() {
    try {
      const normalizedRefCode = this.normalizeRefCode(this.refCode);
      const baseRefCode = this.getBaseRefCode(normalizedRefCode);
      const tagPrefix = `cod:${baseRefCode}`.toLowerCase();

      const queryCandidates = [`cod:${baseRefCode}`, baseRefCode, normalizedRefCode].filter(Boolean);
      const uniqueQueries = [...new Set(queryCandidates)];
      const productsByVariant = new Map();

      for (const query of uniqueQueries) {
        const products = await this.fetchProductsByQuery(query, tagPrefix);
        products.forEach((product) => {
          const key = this.getProductKey(product);
          if (!productsByVariant.has(key)) {
            productsByVariant.set(key, product);
          }
        });
      }

      this.renderProducts(Array.from(productsByVariant.values()));
    } catch (error) {
      console.warn('[related-variations]', error);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.fieldset) return;

    if (!isLoading) {
      this.removeAttribute('aria-busy');
      this.querySelectorAll('.related-variations-skeleton, .related-variations-size-skeleton').forEach((node) =>
        node.remove()
      );
      return;
    }

    this.setAttribute('aria-busy', 'true');
    if (this.querySelector('.related-variations-skeleton')) return;

    const skeleton = document.createElement('div');
    skeleton.className = 'related-variations-skeleton';
    skeleton.setAttribute('role', 'status');
    skeleton.innerHTML = `
      <span class="visually-hidden">Carregando outras variações</span>
      <span class="related-variations-skeleton__item" aria-hidden="true"></span>
      <span class="related-variations-skeleton__item" aria-hidden="true"></span>
      <span class="related-variations-skeleton__item" aria-hidden="true"></span>
    `;
    this.fieldset.insertBefore(skeleton, this.sizeListEl || null);
  }

  async fetchProductsByQuery(query, tagPrefix) {
    const sectionMarkup = await this.fetchSectionMarkup(query);
    if (!sectionMarkup) return [];

    const doc = new DOMParser().parseFromString(sectionMarkup, 'text/html');
    const rows = doc.querySelectorAll('#shopify-section-related-variations-tags [data-handle]');

    return Array.from(rows)
      .map((row) => {
        const tagsRaw = row.dataset.tags || '';
        const tags = tagsRaw
          .split(',')
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean);

        return {
          handle: row.dataset.handle || '',
          variantId: row.dataset.variantId || '',
          title: row.dataset.title || '',
          url: row.dataset.url || '',
          image: row.dataset.image || '',
          size: row.dataset.size || '',
          color: row.dataset.color || '',
          tags,
        };
      })
      .filter((product) => product.handle)
      .filter((product) => this.hasMatchingTag(product.tags, tagPrefix));
  }

  hasMatchingTag(tags, tagPrefix) {
    if (!tagPrefix) return false;
    return tags.some((tag) => tag === tagPrefix || tag.startsWith(`${tagPrefix}.`));
  }

  normalizeRefCode(value) {
    return (value || '').replace(/^cod:/i, '').trim();
  }

  normalizeOptionValue(value) {
    return (value || '').trim().toLowerCase();
  }

  getProductKey(product) {
    return `${product?.handle || ''}:${product?.variantId || product?.url || ''}`;
  }

  isCurrentProduct(product) {
    if (!product || product.handle !== this.currentHandle) return false;
    if (!this.currentVariantId || !product.variantId) return true;
    return String(product.variantId) === this.currentVariantId;
  }

  getBaseRefCode(value) {
    if (!value) return '';
    const lastDotIndex = value.lastIndexOf('.');
    if (lastDotIndex <= 0) return value;
    return value.slice(0, lastDotIndex);
  }

  async fetchSectionMarkup(query) {
    const root = window?.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[limit_scope]': 'all',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,tag,product_type,variants.title,vendor',
      section_id: 'related-variations-tags',
    });

    const response = await fetch(`${root}search/suggest?${params.toString()}`);
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Search suggest failed (${response.status}): ${body}`);
    }

    return response.text();
  }

  renderProducts(products) {
    if (!this.fieldset) return;

    const preparedProducts = this.prepareProducts(products);
    const productsWithCurrent = this.ensureCurrentProduct(preparedProducts);
    if (productsWithCurrent.length === 0) return;

    const sizeGroups = this.groupBySize(productsWithCurrent);
    const selectedSizeKey = this.resolveSelectedSizeKey(sizeGroups, productsWithCurrent);
    const colorProducts = this.pickColorProducts(productsWithCurrent, selectedSizeKey);

    this.clearDynamicContent();
    this.renderSwatches(colorProducts);
    this.renderSizeList(sizeGroups, selectedSizeKey);

    const activeProduct = colorProducts.find((product) => this.isCurrentProduct(product));
    if (this.titleEl && activeProduct?.colorLabel) {
      this.currentTitle = activeProduct.colorLabel;
      this.titleEl.textContent = this.currentTitle;
    }
  }

  prepareProducts(products) {
    const byVariant = new Map();

    products.forEach((product) => {
      if (!product?.handle) return;

      const sizeLabel = (product.size || '').trim();
      const colorLabel = (product.color || '').trim() || this.extractVariationName(product.title);

      const preparedProduct = {
        handle: product.handle,
        variantId: String(product.variantId || ''),
        title: product.title || '',
        url: product.url || `/products/${product.handle}`,
        image: product.image || '',
        sizeLabel,
        sizeKey: this.normalizeOptionValue(sizeLabel),
        colorLabel,
        colorKey: this.normalizeOptionValue(colorLabel),
      };

      byVariant.set(this.getProductKey(preparedProduct), preparedProduct);
    });

    return Array.from(byVariant.values());
  }

  ensureCurrentProduct(products) {
    const nextProducts = [...products];
    const currentIndex = nextProducts.findIndex((product) => this.isCurrentProduct(product));

    const currentAnchor = this.fieldset.querySelector('.related-variation-item--active');
    const currentImage = currentAnchor?.querySelector('img');
    const fallbackCurrent = {
      handle: this.currentHandle,
      variantId: this.currentVariantId,
      title: this.titleEl?.textContent?.trim() || '',
      url: window.location.href,
      image: currentImage?.src || '',
      sizeLabel: this.currentSizeRaw,
      sizeKey: this.currentSizeNormalized,
      colorLabel: this.currentTitle || this.currentColorRaw,
      colorKey: this.normalizeOptionValue(this.currentTitle || this.currentColorRaw),
    };

    if (currentIndex === -1) {
      if (fallbackCurrent.handle) {
        nextProducts.push(fallbackCurrent);
      }
      return nextProducts;
    }

    const currentProduct = { ...nextProducts[currentIndex] };
    if (fallbackCurrent.sizeKey && !currentProduct.sizeKey) {
      currentProduct.sizeLabel = fallbackCurrent.sizeLabel;
      currentProduct.sizeKey = fallbackCurrent.sizeKey;
    }
    if (fallbackCurrent.colorKey && !currentProduct.colorKey) {
      currentProduct.colorLabel = fallbackCurrent.colorLabel;
      currentProduct.colorKey = fallbackCurrent.colorKey;
    }
    if (fallbackCurrent.image && !currentProduct.image) {
      currentProduct.image = fallbackCurrent.image;
    }
    nextProducts[currentIndex] = currentProduct;

    return nextProducts;
  }

  groupBySize(products) {
    const groups = new Map();

    products.forEach((product) => {
      const key = product.sizeKey || '__unknown_size__';
      const label = product.sizeLabel || 'Sem tamanho';

      if (!groups.has(key)) {
        groups.set(key, { key, label, products: [] });
      }
      groups.get(key).products.push(product);
    });

    return groups;
  }

  resolveSelectedSizeKey(sizeGroups, products) {
    if (this.currentSizeNormalized && sizeGroups.has(this.currentSizeNormalized)) {
      return this.currentSizeNormalized;
    }

    const groupWithCurrent = Array.from(sizeGroups.values()).find((group) =>
      group.products.some((product) => this.isCurrentProduct(product))
    );
    if (groupWithCurrent) {
      return groupWithCurrent.key;
    }

    return sizeGroups.keys().next().value || '';
  }

  pickColorProducts(products, preferredSizeKey = '') {
    const colors = new Map();

    products.forEach((product) => {
      const key = product.colorKey || `__handle_${product.handle}`;
      if (!colors.has(key)) {
        colors.set(key, product);
        return;
      }

      const existing = colors.get(key);
      const existingIsCurrent = this.isCurrentProduct(existing);
      const productIsCurrent = this.isCurrentProduct(product);
      const existingHasPreferredSize = Boolean(preferredSizeKey) && existing.sizeKey === preferredSizeKey;
      const productHasPreferredSize = Boolean(preferredSizeKey) && product.sizeKey === preferredSizeKey;

      if (
        (!existingIsCurrent && productIsCurrent) ||
        (!existingIsCurrent && !productIsCurrent && !existingHasPreferredSize && productHasPreferredSize)
      ) {
        colors.set(key, product);
      }
    });

    return Array.from(colors.values());
  }

  pickBestSizeTarget(productsInSize) {
    if (!Array.isArray(productsInSize) || productsInSize.length === 0) return null;

    const sameProductAndColor = productsInSize.find(
      (product) => product.handle === this.currentHandle && product.colorKey === this.currentColorNormalized
    );
    if (sameProductAndColor) return sameProductAndColor;

    const sameProduct = productsInSize.find((product) => product.handle === this.currentHandle);
    if (sameProduct) return sameProduct;

    if (this.currentColorNormalized) {
      const sameColor = productsInSize.find((product) => product.colorKey === this.currentColorNormalized);
      if (sameColor) return sameColor;
    }

    return productsInSize[0];
  }

  isSizeGroupEligible(group) {
    if (!group?.products?.length) return false;

    const hasSameProduct = group.products.some((product) => product.handle === this.currentHandle);
    const hasCurrentColor =
      Boolean(this.currentColorNormalized) &&
      group.products.some((product) => product.colorKey === this.currentColorNormalized);

    return hasSameProduct || hasCurrentColor;
  }

  clearDynamicContent() {
    this.fieldset.querySelectorAll('.related-variation-item').forEach((node) => node.remove());

    if (this.sizeListEl) {
      this.sizeListEl.remove();
      this.sizeListEl = null;
    }
  }

  renderSizeList(sizeGroups, selectedSizeKey) {
    if (!this.fieldset || sizeGroups.size === 0) return;

    this.sizeListEl = document.createElement('div');
    this.sizeListEl.className = 'related-variations-size-list text-subtext product-form__input--button-standard';

    const currentSizeLabel = sizeGroups.get(selectedSizeKey)?.label || '';
    const header = document.createElement('div');
    header.className = 'form__label';
    header.innerHTML = `
      <div class="flex flex-wrap gap-2">
        <span class="font-body-bolder">Tamanho:</span>
        <span class="text-subtext" data-selected-swatch-value="Tamanho">${this.escapeHtml(currentSizeLabel)}</span>
      </div>
    `;
    this.sizeListEl.appendChild(header);

    const options = document.createElement('div');
    options.className = 'related-variations-size-options';

    const sizeEntries = Array.from(sizeGroups.values()).map((group) => {
      return {
        group,
        isEligible: this.isSizeGroupEligible(group),
        isSelected: group.key === selectedSizeKey,
      };
    }).filter((entry) => entry.isEligible);

    sizeEntries.sort((a, b) => {
      if (a.isSelected !== b.isSelected) {
        return a.isSelected ? -1 : 1;
      }
      return 0;
    });

    sizeEntries.forEach(({ group }) => {
      const targetProduct = this.pickBestSizeTarget(group.products);
      if (!targetProduct) return;

      const link = document.createElement('a');
      link.className = 'related-variation-size-link';
      link.href = targetProduct.url;
      link.textContent = group.label;

      if (group.key === selectedSizeKey) {
        link.setAttribute('aria-current', 'page');
      }

      options.appendChild(link);
    });

    this.sizeListEl.appendChild(options);
    this.fieldset.appendChild(this.sizeListEl);
  }

  renderSwatches(products) {
    const imgSize = this.swatchSize * 2;

    products.forEach((product) => {
      const variationName = product.colorLabel || this.extractVariationName(product.title);

      const a = document.createElement('a');
      a.href = product.url || `/products/${product.handle}`;
      a.className = 'related-variation-item';
      if (this.isCurrentProduct(product)) {
        a.classList.add('related-variation-item--active');
        a.setAttribute('aria-current', 'page');
      }
      a.title = variationName || product.title;
      a.setAttribute('data-product-title', variationName || product.title);

      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title;
        img.width = imgSize;
        img.height = imgSize;
        img.loading = product.handle === this.currentHandle ? 'eager' : 'lazy';
        a.appendChild(img);
      }

      this.fieldset.appendChild(a);
    });
  }

  /**
   * Derives a short color/variation label from a full product title.
   */
  extractVariationName(title) {
    const cleanTitle = (title || '').trim();
    if (!cleanTitle) return '';

    const bracketMatch = cleanTitle.match(/\(([^)]+)\)\s*$/);
    if (bracketMatch?.[1]) {
      return bracketMatch[1].trim();
    }

    const separatorPatterns = [/\s[-–—]\s/, /\s\/\s/, /\s\|\s/, /:\s/];
    for (const pattern of separatorPatterns) {
      const parts = cleanTitle.split(pattern).map((part) => part.trim()).filter(Boolean);
      if (parts.length > 1) {
        const candidate = parts[parts.length - 1];
        if (candidate && candidate.toLowerCase() !== cleanTitle.toLowerCase()) {
          return candidate;
        }
      }
    }

    return cleanTitle;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('related-variations')) {
  customElements.define('related-variations', RelatedVariations);
}
